import { Injectable } from '@angular/core';
import { AppTextService } from '@fi-shared/app-text';
import { isUndefined } from 'util';
import {
    AvailableDateFormats,
    AvailableTimeFormats,
    DefaultFormatDateArgs,
    FI_FORMAT_DATE_REGISTRY,
    FiDateFormatOptions,
    FiDateTimeFormatOptions,
    FiDateTimeSecFormatOptions,
    fiGetLocaleCode,
    fiGetUserPreferredDateFormat,
    fiGetUserPreferredTimeFormat,
    FiTimeFormatOptions,
    IAvailableDateTimeFormat,
    IFormatDateArgs,
    isDefined,
} from '@fi-shared/utils';
import { isToday } from '@fi-shared/utils/is-today';
import { FiUserDataService, IFiUserData } from '@fi-shared/fi-shared-services/fi-user-data.service';
import { FiTimeUnitType } from '@fi-shared/fi-format-date/fi-format-date-enums';
import { TimeUnit } from 'ngx-material-timepicker/src/app/material-timepicker/models/time-unit.enum';

const SecondsPerMinute = 60;
const MinutesPerHour = 60;
const HoursPerDay = 24;
const DaysPerMonth = 30;

const MSPerSecond = 1000; // 1000 milliseconds
const MSPerMinute = MSPerSecond * 60;
const MSPerHour = MSPerMinute * 60;
export const MSPerDay = MSPerHour * 24;
const MSPerMonth = MSPerDay * 30;
const MSPerYear = MSPerMonth * 12;

interface IDateTimeFormatNames {
    locale: string;
    label: string;
}

@Injectable()
export class FiFormatDateService {
    readonly timeFormas: IDateTimeFormatNames[];

    private readonly _fiTimeUnitsData: {
        timeUnit: FiTimeUnitType;
        msPerTimeUnit: number;
        //maxTimeUnits: number;
    }[] = [
        {
            timeUnit: FiTimeUnitType.Second,
            msPerTimeUnit: MSPerSecond,
        },
        {
            timeUnit: FiTimeUnitType.Minute,
            msPerTimeUnit: MSPerMinute,
        },
        {
            timeUnit: FiTimeUnitType.Hour,
            msPerTimeUnit: MSPerHour,
        },
        {
            timeUnit: FiTimeUnitType.Day,
            msPerTimeUnit: MSPerDay,
        },
        {
            timeUnit: FiTimeUnitType.Month,
            msPerTimeUnit: MSPerMonth,
        },
        {
            timeUnit: FiTimeUnitType.Year,
            msPerTimeUnit: MSPerYear,
        },
    ];

    constructor(
        private appTextService: AppTextService,
        private fiUserDataService: FiUserDataService,
    ) {
        this.timeFormas = AvailableTimeFormats.map((item) => ({
            locale: item.locale,
            label: this.appTextService.get(item.label),
        }));
    }

    formatDate(
        dateToBeFormatted: Date | string,
        dateFormatOptions: Intl.DateTimeFormatOptions | string,
        args?: IFormatDateArgs,
    ): string {
        if (!isDefined(dateToBeFormatted) || dateToBeFormatted === null) {
            return '';
        }

        args = Object.assign({ ...DefaultFormatDateArgs }, args);
        let format: Intl.DateTimeFormatOptions;
        if (typeof dateFormatOptions === 'string') {
            format = FI_FORMAT_DATE_REGISTRY[dateFormatOptions];
        } else {
            format = dateFormatOptions;
        }

        const date = dateToBeFormatted ? new Date(dateToBeFormatted) : new Date();
        if (isNaN(date.getTime())) {
            // date is not valid
            return format !== FiTimeFormatOptions ? (dateToBeFormatted as string) : '';
        }

        // format time part
        let timeString: string;
        let timeFormat: Intl.DateTimeFormatOptions;
        if (format && (format.hour || format.minute || format.second)) {
            const userPreferredTimeFormat: IAvailableDateTimeFormat = this.getUserPreferredTimeFormat();
            timeFormat = Object.assign({}, userPreferredTimeFormat.options, format ?? {}, {
                year: undefined,
                month: undefined,
                day: undefined,
            });
            timeString = date.toLocaleTimeString(userPreferredTimeFormat.locale, timeFormat);
            if (timeFormat?.hour12) {
                // https://chainformation.atlassian.net/browse/FI-5740
                timeString = timeString.replace(/ AM$/g, ' am').replace(/ PM$/g, ' pm');
            }

            if (!format.month && !format.day) {
                // we should show only time, return it
                return timeString;
            }
        }

        // format date part
        const userPreferredDateFormat: IAvailableDateTimeFormat = this.getUserPreferredDateFormat();
        const dateFormat: Intl.DateTimeFormatOptions = Object.assign(
            {},
            userPreferredDateFormat.options,
            format ?? {},
            {
                hour: undefined,
                minute: undefined,
                second: undefined,
            },
        );
        /*const dateLocale = dateFormat.month === 'short' || dateFormat.month === 'long' || dateFormat.month === 'narrow'
            ? fiGetLocaleCode(this.fiUserDataService.applicationLanguage)
            : userPreferredDateFormat.locale */

        const dateLocale = userPreferredDateFormat.locale;
        let dateString = date.toLocaleDateString(dateLocale, dateFormat);
        if (dateFormat.month === 'short' || dateFormat.month === 'long' || dateFormat.month === 'narrow') {
            // we can't use fiGetLocaleCode(this.fiUserDataService.applicationLanguage) because we always receive date like
            // "JUL 19, 2024" instead "19 JUL 2024"
            // so should replace month name with name in user's system language
            const appLanguageLocale = fiGetLocaleCode(this.fiUserDataService.applicationLanguage);
            const monthDateFormat: Intl.DateTimeFormatOptions = Object.assign({}, dateFormat, {
                day: undefined,
                year: undefined,
            });
            const monthName = date.toLocaleDateString(dateLocale, monthDateFormat);
            const appLanguageMonthName = date.toLocaleDateString(appLanguageLocale, monthDateFormat);
            dateString = dateString.replace(monthName, appLanguageMonthName);
        }
        if (dateFormat.weekday === 'short' || dateFormat.weekday === 'long' || dateFormat.weekday === 'narrow') {
            const appLanguageLocale = fiGetLocaleCode(this.fiUserDataService.applicationLanguage);
            const weekDayDateFormat: Intl.DateTimeFormatOptions = Object.assign({}, dateFormat, {
                day: undefined,
                month: undefined,
                year: undefined,
            });
            const weekDayName = date.toLocaleDateString(dateLocale, weekDayDateFormat);
            const appLanguageWeekDay = date.toLocaleDateString(appLanguageLocale, weekDayDateFormat);
            dateString = dateString.replace(weekDayName, appLanguageWeekDay);
        }

        if (
            !format ||
            format === FiDateTimeFormatOptions ||
            format === FiDateTimeSecFormatOptions ||
            format === FiDateFormatOptions
        ) {
            // check if we can use Today instead date
            if (isToday(date) && this.appTextService && args.showToday) {
                dateString = this.appTextService.get('today');
            }
        }

        let result = dateString;
        if (timeString) {
            result += ', ' + timeString;
        }

        if (args.showUTC && typeof dateToBeFormatted === 'string' && dateToBeFormatted.includes('(UTC')) {
            const dateSplit: string[] = dateToBeFormatted.split('(UTC');
            result = result + ' (UTC' + dateSplit[1];
        }
        return result;
    }

    getUserPreferredDateFormat(): IAvailableDateTimeFormat {
        const userData: IFiUserData = this.fiUserDataService.getUserData();
        const defaultDateFormat: IAvailableDateTimeFormat = fiGetUserPreferredDateFormat(
            DefaultFormatDateArgs.applicationLanguageType,
        );

        // user data is null for confidential agreement dialog
        // https://chainformation.atlassian.net/browse/FI-5456
        const preferredDateFormat = userData?.PreferredDateFormat
            ? AvailableDateFormats.find(
                  (availableDateFormat: IAvailableDateTimeFormat) =>
                      availableDateFormat.locale === userData.PreferredDateFormat,
              )
            : undefined;
        return preferredDateFormat ?? defaultDateFormat;
    }

    getUserPreferredTimeFormat(): IAvailableDateTimeFormat {
        const userData: IFiUserData = this.fiUserDataService.getUserData();
        const defaultTimeFormat: IAvailableDateTimeFormat = fiGetUserPreferredTimeFormat(
            DefaultFormatDateArgs.applicationLanguageType,
        );

        const preferredTimeFormat = userData?.PreferredTimeFormat
            ? AvailableTimeFormats.find(
                  (availableTimeFormat: IAvailableDateTimeFormat) =>
                      availableTimeFormat.locale === userData.PreferredTimeFormat,
              )
            : undefined;
        return preferredTimeFormat ?? defaultTimeFormat;
    }

    // returns time span in {n} months/{n} days/{n} hours format
    getDateDiffText(dateFrom: Date, dateTo: Date) {
        const formatFn = (value: number, singularTextId: string, pluralTextId: string) => {
            return this.appTextService.get(value === 1 ? singularTextId : pluralTextId).replace('{n}', <any>value);
        };

        let months = dateTo.getMonth() - dateFrom.getMonth() + 12 * (dateTo.getFullYear() - dateFrom.getFullYear());
        if (dateTo.getDate() <= dateFrom.getDate()) {
            months -= 1;
        }
        if (months > 0) {
            return formatFn(months, 'checklist.n_month', 'checklist.n_months');
        }
        const millisecondsDiff = dateTo.getTime() - dateFrom.getTime();
        if (millisecondsDiff >= MSPerDay) {
            return formatFn(Math.floor(millisecondsDiff / MSPerDay), 'checklist.n_day', 'checklist.n_days');
        }
        if (millisecondsDiff >= MSPerHour) {
            return formatFn(Math.floor(millisecondsDiff / MSPerHour), 'checklist.n_hour', 'checklist.n_hours');
        }
        return formatFn(Math.floor(millisecondsDiff / MSPerMinute), 'checklist.n_minute', 'checklist.n_minutes');
    }

    getDateYYYYMMDD(date: Date, divider?: string) {
        if (isUndefined(divider)) {
            divider = '-';
        }
        return (
            date.getFullYear() +
            divider +
            ('0' + (date.getMonth() + 1)).slice(-2) +
            divider +
            ('0' + date.getDate()).slice(-2)
        );
    }

    getDateDiffFullText(
        dateFrom: Date,
        dateTo: Date,
        args?: {
            excludeTimeUnits: FiTimeUnitType[];
            includeZeroValues?: boolean;
        },
    ): string {
        /*dateFrom = new Date(2026, 3, 2); // for tests
        dateTo = new Date(2027, 5, 5, 18, 0, 46);*/

        if (!args) {
            args = { excludeTimeUnits: [] };
        }

        const timeUnitsResult: {
            timeUnit: FiTimeUnitType;
            timeUnitsText: string;
            timeUnitsCount: number;
        }[] = [];
        //const parts: string[] = [];
        let diffMs: number = dateTo.getTime() - dateFrom.getTime();

        // fetch count of years, subtract from diffMs proper milliseconds
        // then fetch count of months, subtract from diffMs proper milliseconds etc...
        for (let timeUnitIdx = this._fiTimeUnitsData.length - 1; timeUnitIdx >= 0; timeUnitIdx--) {
            const timeUnitData = this._fiTimeUnitsData[timeUnitIdx];
            const timeUnit = timeUnitData.timeUnit;

            let shouldIncludeTimeUnit =
                (!!args.includeZeroValues || diffMs >= timeUnitData.msPerTimeUnit) &&
                !args.excludeTimeUnits.includes(timeUnit);

            if (shouldIncludeTimeUnit) {
                const timeUnitsCount = Math.floor(diffMs / timeUnitData.msPerTimeUnit);
                const timeUnitsText = this.appTextService
                    .get(this.getTimeUnitTextId(timeUnitData.timeUnit, timeUnitsCount !== 1))
                    .toLowerCase();
                diffMs = diffMs - timeUnitsCount * timeUnitData.msPerTimeUnit;

                timeUnitsResult.push({
                    timeUnit,
                    timeUnitsCount,
                    timeUnitsText,
                });
            }
        }

        return timeUnitsResult.map((item) => `${item.timeUnitsCount} ${item.timeUnitsText}`).join(', ');
    }

    getTimeUnitTextId(timeUnit: FiTimeUnitType, isPlural?: boolean) {
        if (timeUnit === FiTimeUnitType.Second) {
            return isPlural ? 'common.seconds' : 'common.second'; // todo add translations if required
        }
        if (timeUnit === FiTimeUnitType.Minute) {
            return isPlural ? 'common.minutes' : 'common.minute';
        }
        if (timeUnit === FiTimeUnitType.Hour) {
            return isPlural ? 'common.hours' : 'hour';
        }
        if (timeUnit === FiTimeUnitType.Day) {
            return isPlural ? 'common.days' : 'common.day';
        }
        if (timeUnit === FiTimeUnitType.Month) {
            return isPlural ? 'common.months' : 'common.month';
        }
        if (timeUnit === FiTimeUnitType.Year) {
            return isPlural ? 'common.years' : 'common.year';
        }
        throw new Error('Unknown time unit: ' + timeUnit);
    }
}
